import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { openModal } from 'store/modals/actions';
import { fetchDiscountSubscriptions, setCancellationCandidate } from 'store/subscriptions/actions';

import { SUBSCRIPTION_PENDING_FAILED } from 'constants/payments';
import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { cancelFlow_subscriptionScreenLoad } from 'services/analytics/trackers/cancelFlow';

import { useCancelSubscription, useDispatch, useSubscriptions } from 'hooks';
import useSubscriptionsCount from 'hooks/subscriptions/useSubscriptionCount';

import { Box, Text } from 'components/library';
import Skeleton from 'components/Skeleton';
import { PAYMENT_TYPES_NAME } from 'components/SolidPaymentForm/constants';

import { isMainSubscription } from 'pages/AccountSettings/components/SubscriptionsTab/components/SubscriptionCard/helpers';

import { PaymentMethods } from 'types/enums';
import { ModalName } from 'types/modals';
import { ISubscription, PaymentProviders } from 'types/subscription';

import { SubscriptionCard, SubscriptionHelpButton } from './components';
import PaymentFailedCard from './components/SubscriptionCard/components/PaymentFailedCard';

const SubscriptionsTab = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { isLoading, subscriptions } = useSubscriptions();
    const { onCancelSubscription: cancelByForce } = useCancelSubscription();
    const { activeCount, canceledCount } = useSubscriptionsCount(subscriptions);

    useEffect(() => {
        if (subscriptions?.length) {
            cancelFlow_subscriptionScreenLoad({
                reminder: subscriptions.some((i) => i.reminder),
                free_month: subscriptions.some((i) => i.discounts.free_month),
                subscriptions_status: subscriptions.map((i) => ({ [i.external_id]: i.state })),
            });
        }
    }, [subscriptions]);

    const onCancelSubscription = (candidate: ISubscription) => {
        if (candidate.payment_method === PaymentMethods.flexcard || candidate.is_promo_code_used) {
            return cancelByForce(void 0, candidate);
        }

        const hasDiscount = Boolean(candidate.discounts.discount);
        const hasOffer = Boolean(candidate.discounts.pause || candidate.discounts.free_month || candidate.reminder);
        const shouldFetchDiscount = !hasDiscount && !candidate.fee?.fee_enabled;
        const isPaymentFormNeeded =
            candidate.payment_provider === PaymentProviders.Solidgate &&
            [PAYMENT_TYPES_NAME.GOOGLE_PAY, PAYMENT_TYPES_NAME.APPLE_PAY].includes(candidate.payment_method);

        dispatch(setCancellationCandidate(candidate));

        if (shouldFetchDiscount) {
            dispatch(
                fetchDiscountSubscriptions({
                    discount_type: DISCOUNT_TYPES.base,
                    external_id: candidate.external_id,
                    provider: candidate.payment_provider,
                })
            );
        }

        if (candidate.fee?.fee_enabled) {
            dispatch(openModal(isPaymentFormNeeded ? ModalName.FEE_PAYMENT : ModalName.FEE_ALERT));
        } else if (!isMainSubscription(candidate.product_type) || hasDiscount || hasOffer) {
            cancelByForce(void 0, candidate);
        } else {
            dispatch(openModal(ModalName.REASON_MODAL));
        }
    };

    const displaySkeleton = isLoading && !subscriptions?.length;

    const failedFirstIndex = subscriptions.findIndex(
        (subscription) => subscription.state === SUBSCRIPTION_PENDING_FAILED
    );

    return (
        <Box $paddingTop={40} $paddingBottom={40} $paddingX={16} dataLocator="subscriptions-tab-page">
            {displaySkeleton ? (
                <Skeleton height={128} />
            ) : (
                <div>
                    {Boolean(activeCount) && (
                        <Text type="h6" mb={20}>
                            {t('subscription.text.activeCount', { count: activeCount })}
                        </Text>
                    )}
                    {Boolean(canceledCount) && !activeCount && (
                        <Text type="h6" mb={20}>
                            {t('subscription.text.canceledCount', { count: canceledCount })}
                        </Text>
                    )}
                    {subscriptions?.map((subscription: ISubscription, index) => {
                        if (index === failedFirstIndex) {
                            return <PaymentFailedCard key={subscription.id} />;
                        }

                        if (subscription.state !== SUBSCRIPTION_PENDING_FAILED) {
                            return (
                                <div key={subscription.id} data-locator="subscriptionsContainer">
                                    <div key={index} data-locator={`subscription${index}`}>
                                        <SubscriptionCard
                                            subscription={subscription}
                                            onCancelSubscription={onCancelSubscription}
                                        />
                                    </div>
                                </div>
                            );
                        }
                    })}
                </div>
            )}
            <SubscriptionHelpButton />
        </Box>
    );
};

export default SubscriptionsTab;
